import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import { payablesAPI, incomingInvoicesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, IndianRupee, BarChart2, AlertTriangle,
  Clock, CheckCircle, Search, Trash2, Plus, Pencil, X, Building2, ScrollText,
} from 'lucide-react';
import InvoiceReceiptModal from '@/components/InvoiceReceiptModal';

function fmtCur(n) {
  return `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCurShort(n) {
  const v = parseFloat(n || 0);
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

// ── Pay Modal (Mark as Paid) ─────────────────────────────────────────────────
function PayModal({ supplier, onClose, onSaved, editMode = false, payment = null }) {
  const unsettledInvoices = supplier.invoices ?? [];
  const paymentInvoices   = payment?.invoices ?? [];

  const invoices = useMemo(() => {
    if (!editMode) return unsettledInvoices;
    const seen = new Set(paymentInvoices.map(i => i.id));
    return [
      ...paymentInvoices,
      ...unsettledInvoices.filter(i => !seen.has(i.id)),
    ];
  }, [editMode, paymentInvoices, unsettledInvoices]);

  const [selected, setSelected] = useState(() =>
    editMode && payment ? new Set(payment.invoices.map(i => i.id)) : new Set()
  );
  const [amount, setAmount]         = useState(editMode && payment ? String(payment.amount || '') : '');
  const [note, setNote]             = useState(editMode && payment ? (payment.note || '') : '');
  const [mode, setMode]             = useState(editMode && payment ? (payment.payment_mode || 'cash') : 'cash');
  const [saving, setSaving]           = useState(false);
  const [amountEdited, setAmountEdited] = useState(editMode);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);

  const selectedTotal = useMemo(() =>
    invoices.filter(inv => selected.has(inv.id))
             .reduce((s, inv) => s + parseFloat(inv.total_amount || 0), 0),
    [selected, invoices]
  );

  React.useEffect(() => {
    if (!amountEdited) {
      setAmount(selectedTotal > 0 ? String(Math.round(selectedTotal * 100) / 100) : '');
    }
  }, [selectedTotal, amountEdited]);

  const toggleInvoice = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
    setAmountEdited(false);
  };

  const handleViewReceipt = useCallback(async (e, inv) => {
    e.stopPropagation();
    e.preventDefault();
    setPreviewLoading(inv.id);
    try {
      const res = await incomingInvoicesAPI.getOne(inv.id);
      setPreviewInvoice(res.data);
    } catch {
      toast.error('Could not load receipt');
    } finally {
      setPreviewLoading(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (selected.size === 0) { toast.error('Select at least one bill to record payment'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      if (editMode && payment) {
        await payablesAPI.editPayment(payment.id, {
          amount: amt, note, payment_mode: mode,
          invoice_ids: Array.from(selected),
        });
        toast.success('Payment updated');
      } else {
        await payablesAPI.recordPayment(supplier.supplier_portal_id, {
          amount: amt, note, payment_mode: mode,
          invoice_ids: Array.from(selected),
        });
        toast.success(`₹${amt.toLocaleString('en-IN')} marked as paid to ${supplier.supplier_name}`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editMode ? 'Failed to update payment' : 'Failed to record payment'));
    } finally {
      setSaving(false);
    }
  }, [supplier, amount, note, mode, selected, onSaved, editMode, payment]);

  const noneSelected = selected.size === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-sm sm:rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {editMode ? 'Edit Payment' : 'Mark Invoices as Paid'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">to {supplier.supplier_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-4 space-y-4">
          {/* Invoice checklist — only acknowledged, unpaid invoices */}
          {invoices.length > 0 ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Select bills being paid
              </label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {invoices.map(inv => (
                  <label
                    key={inv.id}
                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                      selected.has(inv.id)
                        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(inv.id)}
                      onChange={() => toggleInvoice(inv.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono flex-shrink-0">
                      #{inv.pos_bill_number || '—'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-1">
                      {formatDate(inv.invoice_date)}
                    </span>
                    <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex-shrink-0">
                      {fmtCurShort(inv.total_amount)}
                    </span>
                    <button
                      type="button"
                      onClick={e => handleViewReceipt(e, inv)}
                      disabled={previewLoading === inv.id}
                      className="ml-1 text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                      title="View receipt"
                    >
                      <ScrollText className="w-3.5 h-3.5" />
                    </button>
                  </label>
                ))}
              </div>
              {noneSelected && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Select at least one bill to record payment
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
              No outstanding bills to mark as paid
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Amount paid (₹)
            </label>
            <input
              type="number" min="1" step="1" value={amount}
              onChange={e => { setAmount(e.target.value); setAmountEdited(true); }}
              placeholder="0"
              autoFocus={invoices.length === 0}
              className="w-full text-lg font-semibold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Payment mode</label>
            <div className="flex gap-2 flex-wrap">
              {['cash', 'upi', 'cheque', 'bank_transfer'].map(m => (
                <button key={m} onClick={() => setMode(m)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    mode === m
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {m === 'bank_transfer' ? 'Bank Transfer' : m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Note (optional)</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="e.g. Payment for May invoices"
              className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="px-4 pb-4 flex gap-2 flex-shrink-0 border-t border-gray-100 dark:border-gray-800 pt-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || noneSelected || !amount}
            className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? 'Saving…' : editMode ? 'Update Payment' : 'Record Payment'}
          </button>
        </div>
      </div>
      {previewInvoice && (
        <InvoiceReceiptModal invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />
      )}
    </div>
  );
}

// ── Main SupplierPayables component ──────────────────────────────────────────
export default function SupplierPayables() {
  const qc = useQueryClient();
  const [payablesView, setPayablesView] = useState('outstanding');
  const [search, setSearch]             = useState('');
  const [payTarget, setPayTarget]       = useState(null);
  const [editTarget, setEditTarget]     = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['store-payables'],
    queryFn: () => payablesAPI.getSuppliers().then(r => r.data),
    staleTime: 60_000,
  });

  const suppliers        = data?.suppliers        ?? [];
  const totalOutstanding = data?.total_outstanding ?? 0;

  const supplierPortalIds = suppliers.map(s => s.supplier_portal_id);

  // KPIs
  const suppliersWithBalance = suppliers.filter(s => (s.outstanding ?? 0) > 0.005).length;
  const avgPayable = suppliersWithBalance > 0 ? totalOutstanding / suppliersWithBalance : 0;
  const maxPayable = suppliers.length > 0 ? Math.max(0, ...suppliers.map(s => s.outstanding ?? 0)) : 0;

  const filteredSuppliers = useMemo(() => {
    const q = search.toLowerCase();
    return suppliers
      .filter(s => !q || (s.supplier_name || '').toLowerCase().includes(q))
      .sort((a, b) => (b.outstanding ?? 0) - (a.outstanding ?? 0));
  }, [suppliers, search]);

  // Per-supplier payment history — fetched when Payments Log tab is active
  const historyQueries = useQueries({
    queries: payablesView === 'log' ? supplierPortalIds.map(pid => ({
      queryKey: ['store-payables-history', pid],
      queryFn: () => payablesAPI.getHistory(pid).then(r => r.data),
      staleTime: 60_000,
    })) : [],
  });

  const allPayments = useMemo(() => {
    if (payablesView !== 'log') return [];
    const nameMap = Object.fromEntries(suppliers.map(s => [s.supplier_portal_id, s.supplier_name]));
    const q = search.toLowerCase();
    return historyQueries
      .flatMap((query, i) =>
        (query.data?.payments ?? []).map(p => ({
          ...p,
          supplier_portal_id: supplierPortalIds[i],
          supplier_name: nameMap[supplierPortalIds[i]] || '—',
        }))
      )
      .filter(p => !q || (p.supplier_name || '').toLowerCase().includes(q))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [historyQueries, suppliers, supplierPortalIds, search, payablesView]);

  const logLoading = payablesView === 'log' && historyQueries.some(q => q.isLoading);

  const handlePaid = useCallback(() => {
    setPayTarget(null);
    setEditTarget(null);
    qc.invalidateQueries({ queryKey: ['store-payables'] });
    qc.invalidateQueries({ queryKey: ['store-payables-history'] });
  }, [qc]);

  const handleDeletePayment = useCallback(async (id, portalId) => {
    if (!window.confirm('Delete this payment record? The invoices will be marked as outstanding again.')) return;
    try {
      await payablesAPI.deletePayment(id);
      toast.success('Payment record deleted');
      qc.invalidateQueries({ queryKey: ['store-payables-history', portalId] });
      qc.invalidateQueries({ queryKey: ['store-payables'] });
    } catch {
      toast.error('Could not delete payment');
    }
  }, [qc]);

  return (
    <div className="space-y-5">

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Payable',         value: fmtCur(totalOutstanding), icon: IndianRupee,   color: 'text-amber-600',  bg: 'bg-amber-50'  },
          { label: 'Suppliers Outstanding', value: String(suppliersWithBalance), icon: Building2,    color: 'text-rose-600',   bg: 'bg-rose-50'   },
          { label: 'Avg Payable',           value: fmtCur(Math.round(avgPayable)), icon: BarChart2,    color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Highest Payable',       value: fmtCur(maxPayable),        icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="p-4">
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-2`}>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className="text-xs text-muted-foreground leading-tight">{label}</p>
            <p className={`text-lg font-bold ${color} mt-0.5`}>{isLoading ? '…' : value}</p>
          </Card>
        ))}
      </div>

      {/* Tab toggle + search */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => setPayablesView('outstanding')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
              payablesView === 'outstanding' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="w-3.5 h-3.5" /> Outstanding
          </button>
          <button
            onClick={() => setPayablesView('log')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors border-l ${
              payablesView === 'log' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <CheckCircle className="w-3.5 h-3.5" /> Payments Log
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search supplier…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm" onClick={refetch} disabled={isLoading} className="gap-1.5">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Outstanding tab */}
      {payablesView === 'outstanding' && (
        isLoading ? (
          <p className="text-center py-16 text-muted-foreground">Loading…</p>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No supplier invoices found</p>
            <p className="text-sm mt-1">Acknowledge incoming invoices to track what you owe</p>
          </div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Supplier</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Total Invoiced</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">In Transit</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Outstanding</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSuppliers.map(s => (
                    <tr
                      key={s.supplier_portal_id}
                      className={`hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors ${
                        (s.outstanding ?? 0) <= 0.005 ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">{s.supplier_name}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{fmtCurShort(s.total_invoiced)}</td>
                      <td className="px-4 py-3 text-right text-yellow-700 dark:text-yellow-400">
                        {(s.in_transit_amount ?? 0) > 0.005 ? fmtCurShort(s.in_transit_amount) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-amber-700 dark:text-amber-400">
                        {fmtCurShort(s.outstanding)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setPayTarget(s)}
                          disabled={(s.outstanding ?? 0) <= 0.005}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-3 h-3" /> Mark as Paid
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {/* Payments Log tab */}
      {payablesView === 'log' && (
        logLoading ? (
          <p className="text-center py-16 text-muted-foreground">Loading…</p>
        ) : allPayments.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No payments recorded yet</p>
            <p className="text-sm mt-1">Record payments from the Outstanding tab</p>
          </div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Supplier</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Amount</th>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Mode</th>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Bills</th>
                    <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Note</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {allPayments.map(p => (
                    <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{p.supplier_name}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700 dark:text-emerald-400">
                        ₹{(p.amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground capitalize">
                        {p.payment_mode === 'bank_transfer' ? 'Bank' : p.payment_mode || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {p.invoices?.length > 0
                          ? p.invoices.map(i => `#${i.pos_bill_number || '—'}`).join(', ')
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{p.note || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => {
                              const sup = suppliers.find(s => s.supplier_portal_id === p.supplier_portal_id)
                                ?? { supplier_portal_id: p.supplier_portal_id, supplier_name: p.supplier_name, invoices: [] };
                              setEditTarget({ payment: p, supplier: sup });
                            }}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Edit payment"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeletePayment(p.id, p.supplier_portal_id)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Delete payment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {/* Mark as Paid modal */}
      {payTarget && (
        <PayModal
          supplier={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={handlePaid}
        />
      )}

      {/* Edit payment modal */}
      {editTarget && (
        <PayModal
          supplier={editTarget.supplier}
          payment={editTarget.payment}
          editMode={true}
          onClose={() => setEditTarget(null)}
          onSaved={handlePaid}
        />
      )}
    </div>
  );
}
